<template>
    <div id="canvas" ref="container" @contextmenu="($event) => $event.preventDefault()">
        <div v-if="loading || reloading" class="d-flex justify-content-center align-items-center w-100">
            <b-spinner small label="Loading" />
        </div>
        <v-stage ref="stage" v-show="stageConfig && !loading && !reloading" :config="stageConfig">
            <v-layer v-if="whiteBg">
                <v-rect ref="bg-rect" :config="bgRectConfig" />
            </v-layer>
            <v-layer>
                <v-image :config="imageConfig" ref="image" />
            </v-layer>
        </v-stage>
        <transition name="fade">
            <div ref="vp-tooltip" v-show="enableViewpointNameTooltip && isShowingViewpointTooltip" class="viewpoint-tooltip">
                <p class="text-12 text-weight-bold">
                    {{ viewpoints.find((vp) => vp.id == hoveredViewpoint) && viewpoints.find((vp) => vp.id == hoveredViewpoint).name }}
                </p>
            </div>
        </transition>
        <transition name="fade">
            <div ref="sector-tooltip" v-show="enableSectorNameTooltip && isShowingSectorTooltip" class="sector-tooltip">
                <!-- <p class="text-12 text-weight-bold">{{hoveredSector.name}}</p> -->
                <p class="text-12 text-weight-bold">
                    {{ sectors.find((sec) => sec.id == hoveredSector) && sectors.find((sec) => sec.id == hoveredSector).name }}
                </p>
            </div>
        </transition>
    </div>
</template>

<script>
const MAX_VIEWPOINT_SIZE = 16
const MIN_VIEWPOINT_SIZE = 10

import imgIcon from '@/assets/img/misc/img-icon.png'
import trashIcon from '@/assets/img/misc/connection-trash.png'
import debounce from 'debounce'

export default {
    props: {
        floorplan: {
            type: String,
            required: true,
        },
        draggable: {
            type: Boolean,
            default: true,
        },
        floorplansOpacity: {
            type: Number,
            default: 100,
        },
        // Sector
        sectors: {
            type: Array,
            default: () => [],
        },
        grayscaleOnSectorSelected: {
            type: Boolean,
            default: true,
        },
        selectSectorByProp: {
            type: Boolean,
            default: false,
        },
        selectedSector: {
            type: Number,
            default: -1,
        },
        anchorSelectedSector: {
            type: Boolean,
            default: false,
        },
        enableSectorNameTooltip: {
            type: Boolean,
            default: false,
        },
        // Viewpoints
        viewpoints: {
            type: Array,
            default: () => [],
        },
        viewpointSize: {
            type: Number,
            default: 2,
        },
        enableViewpointHover: {
            type: Boolean,
            default: true,
        },
        enableViewpointTooltip: {
            type: Boolean,
            default: false,
        },
        enableViewpointNameTooltip: {
            type: Boolean,
            default: false,
        },
        enableConnectionsDelete: Boolean,
        grayscaleOnViewpointSelected: {
            type: Boolean,
            default: true,
        },
        selectedViewpoint: {
            default: -1,
        },
        hoveredViewpoint: {
            default: -1,
        },
        hoveredSector: {
            default: -1,
        },
        selectViewpointByProp: {
            type: Boolean,
            default: false,
        },
        draggableSelectedViewpoint: {
            type: Boolean,
            default: false,
        },
        // config
        draggableStage: {
            type: Boolean,
            default: true,
        },
        minZoom: {
            type: Number,
            default: 1,
        },
        maxZoom: {
            type: Number,
            default: 5,
        },
        zoomFactor: {
            type: Number,
            default: 1.25,
        },
        // filter
        datesToFilter: {
            type: Array,
        },

        // Virtual Tour
        // Obs: An array containing objects like: {viewpoint: {...}, connectsTo: [{...vp}]}
        connections: Array,
        highlightedConnections: Array,
        islands: Array,

        // An array containing the vps ids
        grayViewpoints: Array,
        grayConnections: Array,

        whiteBg: Boolean,
        connectionLineWidth: {
            type: Number,
            default: 7,
        },
    },
    data() {
        return {
            loading: false,
            reloading: false,
            stage: null,
            stageConfig: {},
            mainLayer: null,
            image: null,
            proportionX: 1,
            proportionY: 1,
            imageConfig: {},
            bgRectConfig: {},
            imageNode: null,
            imageAsset: null,
            imgIconNode: null,
            trashIconNode: null,
            trashAsset: null,
            sectorNodes: [],
            sectorOrigin: null,
            anchorOrigin: null,
            viewpointNodes: [],
            viewpointOrigin: null,
            innerViewpointOrigin: null,
            tooltipNode: null,
            dotSize: 0,
            colorMap: {
                primary: '#18181A',
                blue: '#1EB3FD',
                white: '#FFFFFF',
                gray: '#808080',
                high: '#FB0335',
                medium: '#FFA800',
                low: '#1EB3FD',
            },
            scale: 1,
            lastPinchDistance: null,
            isShowingViewpointTooltip: false,
            isShowingSectorTooltip: false,
            viewpointsFilterFalse: [],
            sectorsFilterFalse: [],

            // Virtual tour
            lastConnectionNode: null,
            lastViewpointFromNode: null,
            lastViewpointToNode: null,
        }
    },
    watch: {
        floorplan() {
            this.reloading = true
            this.sectorOrigin = null
            this.anchorOrigin = null
            this.$nextTick(async () => {
                await this.loadImage()
                this.configImage()
                this.configSectors()
                this.configViewpoints2()
                this.configConnections()
                this.resetScale()
                this.stage.batchDraw()
                this.reloading = false
                this.applyFilters()
            })
        },
        highlightedConnections() {
            if (this.stage) {
                this.configConnections()
                this.stage.batchDraw()
            }
        },
        connections() {
            if (this.stage) {
                this.configConnections()
                this.stage.batchDraw()
            }
        },
        grayConnections() {
            if (this.stage) {
                this.configConnections()
                this.stage.batchDraw()
            }
        },
        islands() {
            if (this.stage) {
                this.configViewpoints2()
                this.stage.batchDraw()
            }
        },
        grayViewpoints() {
            if (this.stage) {
                this.configViewpoints2()
                if (this.connections && this.connections.length) {
                    this.configConnections()
                }
                this.stage.batchDraw()
            }
        },
        sectors() {
            if (!this.stage) {
                return
            }

            this.sectorNodes
                .filter((node) => {
                    return !this.sectors.find((sec) => sec.id == node.getId())
                })
                .forEach((node) => {
                    if (node.anchors && node.anchors.length) {
                        node.anchors.forEach((anchor) => {
                            anchor.destroy()
                        })
                    }
                    node.destroy()
                    this.sectorNodes.splice(this.sectorNodes.indexOf(node), 1)
                })

            let noSectorSelected = this.noSectorSelected()
            this.sectors
                .filter((sec) => {
                    return !this.sectorNodes.find((node) => sec.id == node.getId())
                })
                .forEach((sector) => {
                    let isSelected = noSectorSelected || this.isSectorSelected(sector)

                    let clone = this.sectorOrigin.clone({
                        id: sector.id,
                        points: sector.coords.reduce((arr, coord) => {
                            arr.push(coord.x * this.proportionX, coord.y * this.proportionY)
                            return arr
                        }, []),
                        // stroke: this.colorMap.primary,
                        stroke: this.isSectorSelected(sector) ? this.colorMap.blue : this.colorMap.primary,
                        strokeWidth: this.isSectorSelected(sector) ? 4 : 2,
                        fill: this.isSectorSelected(sector) ? this.colorMap.white : this.colorMap.blue,
                        perfectDrawEnabled: false,
                    })

                    // clone.cache()
                    this.sectorNodes.push(clone)
                    this.mainLayer.add(clone)

                    if (this.anchorSelectedSector && !noSectorSelected && this.isSectorSelected(sector)) {
                        clone.anchors = []
                        sector.coords.forEach((coord) => {
                            let anchor = this.anchorOrigin.clone({
                                id: 'anchor',
                                x: this.imageConfig.position.x + coord.x * this.proportionX,
                                y: this.imageConfig.position.y + coord.y * this.proportionY,
                            })

                            clone.anchors.push(anchor)
                            this.mainLayer.add(anchor)
                        })
                    }
                })

            this.stage.batchDraw()
        },
        selectedSector() {
            if (!this.selectSectorByProp) {
                if (this.selectedSector == -1) {
                    this.imageNode.opacity(this.floorplansOpacity / 100)
                    this.sectorNodes.forEach((node, index) => {
                        node.fill(this.colorMap.blue)
                        node.strokeWidth(2)
                        node.stroke(this.colorMap.primary)
                    })
                    this.tooltipNode.remove()
                } else {
                    this.sectorNodes.forEach((node) => {
                        if (this.selectedSector == node.getId()) {
                            node.fill(this.colorMap.white)
                            node.strokeWidth(4)
                            node.stroke(this.colorMap.blue)
                        } else {
                            node.fill(this.colorMap.blue)
                            node.strokeWidth(2)
                            node.stroke(this.colorMap.primary)
                        }
                    })
                    if (this.grayscaleOnSectorSelected && this.imageNode) {
                        this.imageNode.opacity(0.5)
                    }
                }
                this.stage.batchDraw()
            }
        },
        hoveredSector() {
            this.sectorNodes.forEach((node, index) => {
                let sector = this.sectors.find((sec) => sec.id == node.getId())
                // Hovered
                if (this.hoveredSector == node.getId()) {
                    node.opacity(0.95)
                    node.zIndex(this.sectorNodes.length)
                }
                // Not hovered
                else {
                    node.opacity(0.6)
                    node.zIndex(this.sectorNodes.indexOf(node) + 1)
                }

                // Not matching with filter
                if (this.isSectorInSectorsFilterFalse(node.getId())) {
                    // Selected
                    if (this.selectedSector == node.getId()) {
                        node.fill(this.colorMap.white)
                        node.stroke(this.colorMap.gray)
                        node.strokeWidth(4)
                    }
                    // Not selected
                    else {
                        node.fill(this.colorMap.gray)
                        node.stroke(this.colorMap.primary)
                        node.strokeWidth(2)
                    }
                }
                // Matching with filter
                else {
                    // Selected
                    if (this.selectedSector == node.getId()) {
                        node.fill(this.colorMap.white)
                        node.stroke(this.checkSectorDate(sector) ? this.colorMap.blue : this.colorMap.gray)
                        node.strokeWidth(4)
                    }
                    // Not selected
                    else {
                        node.fill(this.checkSectorDate(sector) ? this.colorMap.blue : this.colorMap.gray)
                        node.stroke(this.colorMap.primary)
                        node.strokeWidth(2)
                    }
                }

                node.draw()
            })

            this.stage.batchDraw()
        },
        viewpoints() {
            if (this.imageConfig.position) {
                this.viewpointNodes
                    .filter((node) => {
                        return !this.viewpoints.find((viewpoint) => viewpoint.id == node.getId())
                    })
                    .forEach((node) => {
                        node.destroy()
                        this.viewpointNodes.splice(this.viewpointNodes.indexOf(node), 1)
                    })

                let { x, y } = this.imageConfig.position
                let noViewpointSelected = this.noViewpointSelected()
                let scale = 0.1 / (4 - this.viewpointSize)

                if (this.viewpointSize == 1) {
                    scale = 0.02
                }

                this.viewpoints
                    .filter((viewpoint) => {
                        return !this.viewpointNodes.find((node) => viewpoint.id == node.getId())
                    })
                    .forEach((viewpoint, index) => {
                        let isSelected = this.isViewpointSelected(viewpoint)

                        let group = new Konva.Group({
                            id: viewpoint.id,
                            name: 'viewpoint',
                            x: Math.round(x + (viewpoint.x / this.image.width) * (this.imageConfig.width * this.imageConfig.scale.x)),
                            y: Math.round(y + (viewpoint.y / this.image.height) * (this.imageConfig.height * this.imageConfig.scale.x)),
                            scale: isSelected ? { x: scale * 1.25, y: scale * 1.25 } : { x: scale, y: scale },
                        })

                        let outerClone = this.viewpointOrigin.clone({
                            id: viewpoint.id,
                            fill: this.colorMap.blue,
                            stroke: this.colorMap.primary,
                            opacity: isSelected ? 1 : 0,
                        })

                        let innerClone = this.innerViewpointOrigin.clone({
                            fill: isSelected ? this.colorMap.white : this.colorMap.blue,
                            stroke: isSelected ? '' : this.colorMap.primary,
                            scale: isSelected ? { x: 0.5, y: 0.5 } : { x: 1, y: 1 },
                        })

                        group.add(outerClone)
                        group.add(innerClone)

                        this.viewpointNodes.push(group)
                        this.mainLayer.add(group)
                    })

                if (!noViewpointSelected) {
                    this.viewpointNodes.forEach((node, index) => {
                        let viewpoint = this.viewpoints.find((vp) => vp.id == node.getId())
                        if (this.isViewpointSelected(viewpoint)) {
                            node.zIndex(this.viewpoints.length)
                        } else {
                            node.zIndex(index + 1)
                        }
                    })
                }

                this.zIndexConfig()

                this.stage.batchDraw()
            }
        },
        selectedViewpoint() {
            if (!this.selectViewpointByProp) {
                if (!this.selectedViewpoint || this.selectedViewpoint == -1) {
                    this.imageNode.opacity(this.floorplansOpacity / 100)

                    this.viewpointNodes.forEach((node, index) => {
                        let viewpoint = this.viewpoints.find((v) => v.id == node.getId())
                        let scale = 0.1 / (4 - this.viewpointSize)

                        if (this.viewpointSize == 1) {
                            scale = 0.02
                        }

                        // node.fill(viewpoint.new ? this.colorMap.white : this.colorMap.primary)
                        // node.stroke(this.colorMap.blue)
                        // node.zIndex(index + 1)
                        // node.opacity(0.8)
                        // node.draggable(false)
                        let outerCircle = node.children[0]
                        let innerCircle = node.children[1]

                        node.scale({ x: scale, y: scale })
                        outerCircle.opacity(0)
                        innerCircle.scale({ x: 1, y: 1 })
                        innerCircle.strokeWidth((this.dotSize * 10) / 5)

                        let fillColor = this.colorMap.blue

                        if (this.grayViewpoints && this.grayViewpoints.length && this.grayViewpoints.some((id) => id == viewpoint.id)) {
                            fillColor = this.colorMap.gray
                        }

                        innerCircle.fill(fillColor)
                        innerCircle.stroke(this.colorMap.primary)
                    })
                    this.tooltipNode.remove()
                } else {
                    this.viewpointNodes.forEach((node) => {
                        let viewpoint = this.viewpoints.find((v) => v.id == node.getId())
                        let scale = 0.1 / (4 - this.viewpointSize)
                        if (this.viewpointSize == 1) {
                            scale = 0.02
                        }
                        let outerCircle = node.children[0]
                        let innerCircle = node.children[1]

                        if (this.selectedViewpoint == node.getId()) {
                            // node.fill(viewpoint.new ? this.colorMap.white : this.colorMap.blue)
                            // node.stroke(viewpoint.new ? this.colorMap.blue : this.colorMap.primary)
                            // node.zIndex(this.viewpointNodes.length)
                            // node.opacity(1)

                            node.scale({ x: scale * 1.25, y: scale * 1.25 })
                            // outerCircle.fill(this.colorMap.blue)
                            outerCircle.stroke(this.colorMap.primary)
                            outerCircle.opacity(1)

                            // innerCircle.fill(this.colorMap.white)
                            innerCircle.scale({ x: 0.5, y: 0.5 })
                            innerCircle.stroke('')

                            if (
                                this.isViewpointInViewpointsFilterFalse(node.getId()) ||
                                (this.grayViewpoints && this.grayViewpoints.length && this.grayViewpoints.some((id) => id == viewpoint.id))
                            ) {
                                outerCircle.fill(this.colorMap.gray)
                                innerCircle.fill(this.colorMap.white)
                            } else {
                                outerCircle.fill(this.colorMap.blue)
                                innerCircle.fill(this.colorMap.white)
                            }

                            if (this.draggableSelectedViewpoint) {
                                node.draggable(true)
                            }
                            if (this.enableViewpointTooltip) {
                                this.showTooltip(
                                    this.viewpoints.find((vp) => vp.id == this.selectedViewpoint),
                                    node
                                )
                            }
                        } else {
                            node.scale({ x: scale, y: scale })
                            outerCircle.opacity(0)
                            // innerCircle.fill(this.colorMap.blue)
                            innerCircle.stroke(this.colorMap.primary)
                            innerCircle.scale({ x: 1, y: 1 })

                            if (
                                this.isViewpointInViewpointsFilterFalse(node.getId()) ||
                                (this.grayViewpoints && this.grayViewpoints.length && this.grayViewpoints.some((id) => id == viewpoint.id))
                            ) {
                                innerCircle.fill(this.colorMap.gray)
                            } else {
                                innerCircle.fill(this.colorMap.blue)
                            }
                        }
                    })
                    if (this.grayscaleOnViewpointSelected) {
                        this.imageNode.opacity(this.floorplansOpacity / 100 / 2)
                    }
                }

                this.stage.batchDraw()
            }
        },
        hoveredViewpoint() {
            this.viewpointNodes.forEach((node, index) => {
                // let scale = 0.1 / (4 - this.viewpointSize)
                let outerCircle = node.children[0]
                let innerCircle = node.children[1]

                if (
                    this.isViewpointInViewpointsFilterFalse(node.getId()) ||
                    (this.grayViewpoints && this.grayViewpoints.length && this.grayViewpoints.some((id) => id == node.getId()))
                ) {
                    if (this.selectedViewpoint == node.getId()) {
                        outerCircle.fill(this.colorMap.gray)
                        innerCircle.fill(this.colorMap.white)
                    } else {
                        innerCircle.fill(this.colorMap.gray)
                    }
                } else {
                    if (this.selectedViewpoint == node.getId()) {
                        outerCircle.fill(this.colorMap.blue)
                        innerCircle.fill(this.colorMap.white)
                    } else {
                        innerCircle.fill(this.colorMap.blue)
                    }
                }

                if (this.hoveredViewpoint == node.getId() && this.hoveredViewpoint != this.selectedViewpoint) {
                    var scale = 2
                    innerCircle.scale({ x: scale, y: scale })
                } else {
                    if (node.getId() == this.selectedViewpoint) {
                        var scale = 0.5
                        innerCircle.scale({ x: scale, y: scale })
                    } else {
                        var scale = 1
                        innerCircle.scale({ x: scale, y: scale })
                    }
                }

                if (this.selectedViewpoint == node.getId()) {
                    node.parent.zIndex(this.viewpointNodes.length)
                } else {
                    node.parent.zIndex(this.viewpointNodes.indexOf(node) + 1)
                }

                node.draw()
            })

            this.stage.batchDraw()
        },
        viewpointSize() {
            let scale = 0.1 / (4 - this.viewpointSize)

            if (this.viewpointSize == 1) {
                scale = 0.02
            }

            this.viewpointNodes.forEach((node) => {
                if (this.selectedViewpoint == node.getId()) {
                    node.scale({ x: scale * 1.25, y: scale * 1.25 })
                } else {
                    node.scale({ x: scale, y: scale })
                }
            })

            this.stage.batchDraw()
        },
        datesToFilter: {
            handler() {
                this.applyFilters()
            },
            deep: true,
        },
        floorplansOpacity() {
            this.imageNode.opacity(this.floorplansOpacity / 100)
            this.stage.batchDraw()
        },
    },
    methods: {
        // Filters
        filterViewpoints() {
            this.viewpointsFilterFalse = []
            this.viewpoints.forEach((viewpoint) => {
                if (!this.checkViewpointDate(viewpoint)) {
                    this.viewpointsFilterFalse.push(viewpoint)
                }
            })
        },
        checkViewpointDate(viewpoint) {
            if (this.datesToFilter) {
                if (this.datesToFilter.length == 0) return true
                let flag = false
                viewpoint.images.forEach((img) => {
                    let imgDate = img.date.split(' ')[0]
                    this.datesToFilter.forEach((date) => {
                        if (date == imgDate) {
                            flag = true
                        }
                    })
                })
                return flag
            }

            return true
        },
        isViewpointInViewpointsFilterFalse(viewpoint) {
            let aux = false
            this.viewpointsFilterFalse.forEach((vp) => {
                if (vp.id == viewpoint) {
                    aux = true
                }
            })
            return aux
        },
        filterSectors() {
            this.sectorsFilterFalse = []
            this.sectors.forEach((sector) => {
                if (!this.checkSectorDate(sector)) {
                    this.sectorsFilterFalse.push(sector)
                }
            })
        },
        checkSectorDate(sector) {
            if (this.datesToFilter && this.datesToFilter.length == 0) return true
            let flag = false
            if (sector.floorplans) {
                sector.floorplans.forEach((fp) => {
                    if (fp.viewpoints) {
                        fp.viewpoints.forEach((vp) => {
                            vp.images.forEach((img) => {
                                let imgDate = img.date.split(' ')[0]
                                this.datesToFilter.forEach((date) => {
                                    if (date == imgDate) {
                                        flag = true
                                    }
                                })
                            })
                        })
                    }
                })
            }
            return flag
        },
        isSectorInSectorsFilterFalse(sector) {
            let aux = false
            this.sectorsFilterFalse.forEach((sec) => {
                if (sec.id == sector) {
                    aux = true
                }
            })
            return aux
        },
        applyFilters() {
            // Filter Viewpoints
            this.filterViewpoints()
            this.viewpointNodes.forEach((node) => {
                let viewpoint = this.viewpoints.find((v) => v.id == node.getId())
                let outerCircle = node.children[0]
                let innerCircle = node.children[1]

                // Not matching with filter
                if (this.isViewpointInViewpointsFilterFalse(node.getId())) {
                    if (this.selectedViewpoint == node.getId()) {
                        outerCircle.fill(this.colorMap.gray)
                        innerCircle.fill(this.colorMap.white)
                    } else {
                        innerCircle.fill(this.colorMap.gray)
                    }
                }
                // Matching with filter
                else {
                    if (this.selectedViewpoint == node.getId()) {
                        outerCircle.fill(this.colorMap.blue)
                        innerCircle.fill(this.colorMap.white)
                    } else {
                        innerCircle.fill(this.colorMap.blue)
                    }
                }
            })

            // Filter Sectors
            this.filterSectors()
            this.sectorNodes.forEach((node) => {
                let sector = this.sectors.find((s) => s.id == node.getId())

                // Not matching with filter
                if (this.isSectorInSectorsFilterFalse(node.getId())) {
                    // Selected
                    if (this.selectedSector == node.getId()) {
                        node.fill(this.colorMap.white)
                        node.stroke(this.colorMap.gray)
                        node.strokeWidth(4)
                    }
                    // Not selected
                    else {
                        node.fill(this.colorMap.gray)
                        node.stroke(this.colorMap.primary)
                        node.strokeWidth(2)
                    }
                }
                // Matching with filter
                else {
                    // Selected
                    if (this.selectedSector == node.getId()) {
                        node.fill(this.colorMap.white)
                        node.stroke(this.colorMap.blue)
                        node.strokeWidth(4)
                    }
                    // Not selected
                    else {
                        node.fill(this.colorMap.blue)
                        node.stroke(this.colorMap.primary)
                        node.strokeWidth(2)
                    }
                }
            })

            if (this.stage) this.stage.batchDraw()
        },
        // Setup
        async setupCanvas() {
            this.loading = true
            this.$nextTick(async () => {
                await Promise.all([this.loadImage(), this.loadImgIconAsset(), this.loadTrashIconAsset()])
                await this.configStage()
                this.configImage()
                this.$nextTick(() => {
                    this.configViewpointSize()
                    this.configZoom()
                    this.configSectors()
                    this.configViewpoints2()
                    this.configConnections()
                    this.configHover()
                    this.configClick()
                    this.configDrag()
                    this.configTooltip()
                    this.stage.batchDraw()
                    this.applyFilters()

                    this.$emit('canvasLoaded')
                    this.loading = false
                })
            })
        },
        setupCanvasResize() {
            this.loading = true
            this.$nextTick(async () => {
                await this.configStage()
                this.configImage()
                this.$nextTick(() => {
                    this.configViewpointSize()
                    this.configZoom()
                    this.configSectors()
                    this.configViewpoints2()
                    this.configConnections()
                    this.configHover()
                    this.configClick()
                    this.configDrag()
                    this.configTooltip()
                    this.stage.batchDraw()
                    this.$emit('canvasLoaded')
                    this.loading = false
                })
            })
        },
        loadImage() {
            return new Promise((resolve) => {
                let image = new Image()
                image.crossOrigin = 'Anonymous'
                image.onload = () => {
                    this.image = image
                    resolve()
                }
                image.src = this.floorplan
            })
        },
        loadImgIconAsset() {
            return new Promise((resolve) => {
                let image = new Image()
                image.onload = () => {
                    this.imageAsset = image
                    this.imgIconNode = new Konva.Image({
                        id: 'img-icon',
                        image: this.imageAsset,
                        scale: { x: 15 / this.imageAsset.width, y: 15 / this.imageAsset.width },
                    })
                    resolve()
                }
                image.src = imgIcon
            })
        },
        configStage() {
            return new Promise((resolve) => {
                let { clientWidth, clientHeight } = this.$refs.container
                this.stageConfig = {
                    width: clientWidth,
                    height: clientHeight,
                    draggable: this.draggableStage,
                }
                this.$nextTick(() => {
                    this.stage = this.$refs.stage.getStage()
                    // this.stage.container().style.cursor = "move"
                    resolve()
                })
            })
        },
        configImage() {
            let scale = 1
            let { clientWidth, clientHeight } = this.$refs.container
            let { width, height } = this.image
            if (width * scale > clientWidth) {
                scale = clientWidth / width
            }
            if (height * scale > clientHeight) {
                scale = clientHeight / height
            }

            this.imageConfig = {
                image: this.image,
                opacity: 1,
                width,
                height,
                scale: { x: scale, y: scale },
                position: {
                    x: (clientWidth - width * scale) / 2,
                    y: (clientHeight - height * scale) / 2,
                },
                filters: [Konva.Filters.HSL],
            }

            this.bgRectConfig = {
                opacity: 1,
                width,
                height,
                scale: { x: scale, y: scale },
                position: {
                    x: (clientWidth - width * scale) / 2,
                    y: (clientHeight - height * scale) / 2,
                },
                fill: 'white',
                zIndex: -1,
            }

            this.proportionX = (this.imageConfig.width * this.imageConfig.scale.x) / this.image.width
            this.proportionY = (this.imageConfig.height * this.imageConfig.scale.y) / this.image.height
            this.cacheImageNode()
        },
        cacheImageNode() {
            this.$nextTick(() => {
                this.imageNode = this.$refs.image.getStage()
                this.mainLayer = this.imageNode.getLayer()

                this.stage.batchDraw()
            })
        },
        configZoom() {
            this.stage.on('wheel', (e) => {
                e.evt.stopPropagation()

                let pointer = this.stage.getPointerPosition()
                if (e.evt.deltaY < 0) {
                    this.scaleUp(pointer)
                    if (this.dotSize > MIN_VIEWPOINT_SIZE) {
                        this.dotSize = this.dotSize / 1.2
                    }
                } else {
                    this.scaleDown(pointer)
                    if (this.dotSize < MAX_VIEWPOINT_SIZE) {
                        this.dotSize = this.dotSize * 1.2
                    }
                }
            })

            this.stage.on('touchmove', (e) => {
                if (e.evt.touches && e.evt.touches.length > 1) {
                    e.evt.preventDefault()
                    e.evt.stopPropagation()

                    let firstTouch = e.evt.touches[0]
                    let secondTouch = e.evt.touches[1]
                    let distance = this.getPinchDistance({ x: firstTouch.clientX, y: firstTouch.clientY }, { x: secondTouch.clientX, y: secondTouch.clientY })

                    if (!this.lastPinchDistance) {
                        this.lastPinchDistance = distance
                    }

                    let scale = (this.scale * distance) / (this.lastPinchDistance * 2)
                    this.scaleCanvas(scale, {
                        x: this.stageConfig.width / 2,
                        y: this.stageConfig.height / 2,
                    })
                }
            })

            this.stage.on('touchend', (e) => {
                this.lastPinchDistance = null
            })
        },
        setupSectorOrigin() {
            this.sectorOrigin = new Konva.Line({
                x: this.imageConfig.position.x,
                y: this.imageConfig.position.y,
                closed: true,
                stroke: this.colorMap.primary,
                fill: this.colorMap.blue,
                opacity: 0.6,
            })

            this.anchorOrigin = new Konva.Circle({
                radius: 4,
                strokeWidth: 2,
                fill: this.colorMap.blue,
                stroke: this.colorMap.primary,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
            })
        },
        configSectors() {
            if (!this.sectorOrigin || !this.anchorOrigin) {
                this.setupSectorOrigin()
            }
            if (this.sectorNodes.length) {
                this.sectorNodes.forEach((node) => {
                    node.destroy()
                })
                this.sectorNodes = []
            }

            let noSectorSelected = this.noSectorSelected()
            this.sectors.forEach((sector) => {
                let clone = this.sectorOrigin.clone({
                    id: sector.id,
                    points: sector.coords.reduce((arr, coord) => {
                        arr.push(coord.x * this.proportionX, coord.y * this.proportionY)
                        return arr
                    }, []),
                    stroke: this.isSectorSelected(sector) ? (this.checkSectorDate(sector) ? this.colorMap.blue : this.colorMap.gray) : this.colorMap.primary,
                    strokeWidth: this.isSectorSelected(sector) ? 4 : 2,
                    fill: this.isSectorSelected(sector) ? this.colorMap.white : this.checkSectorDate(sector) ? this.colorMap.blue : this.colorMap.gray,
                    perfectDrawEnabled: false,
                })

                // clone.cache()
                this.sectorNodes.push(clone)
                this.mainLayer.add(clone)

                if (this.anchorSelectedSector && !noSectorSelected && this.isSectorSelected(sector)) {
                    clone.anchors = []
                    sector.coords.forEach((coord) => {
                        let anchor = this.anchorOrigin.clone({
                            id: 'anchor',
                            x: this.imageConfig.position.x + coord.x * this.proportionX,
                            y: this.imageConfig.position.y + coord.y * this.proportionY,
                        })

                        clone.anchors.push(anchor)
                        this.mainLayer.add(anchor)
                    })
                }
            })
        },
        redrawSector() {
            let noSectorSelected = this.noSectorSelected()
            ;[].forEach.call(arguments, (id) => {
                let node = this.sectorNodes.find((node) => node.getId() == id)
                if (node) {
                    let sector = this.sectors.find((sec) => sec.id == id)
                    if (sector) {
                        if (node.anchors && node.anchors.length) {
                            node.anchors.forEach((anchor) => {
                                anchor.destroy()
                            })
                        }
                        if (this.anchorSelectedSector && !noSectorSelected && this.isSectorSelected(sector)) {
                            node.anchors = []
                            sector.coords.forEach((coord) => {
                                let anchor = this.anchorOrigin.clone({
                                    id: 'anchor',
                                    x: this.imageConfig.position.x + coord.x * this.proportionX,
                                    y: this.imageConfig.position.y + coord.y * this.proportionY,
                                })

                                node.anchors.push(anchor)
                                this.mainLayer.add(anchor)
                            })
                        }

                        node.points(
                            sector.coords.reduce((arr, coord) => {
                                arr.push(coord.x * this.proportionX, coord.y * this.proportionY)
                                return arr
                            }, [])
                        )
                        if (noSectorSelected || this.isSectorSelected(sector)) {
                            // node.fill(this.colorMap.blue)
                            node.fill(this.colorMap.white)
                            node.stroke(this.colorMap.blue)
                            node.strokeWidth(4)
                        } else {
                            node.fill(this.colorMap.gray)
                            // node.fill(this.colorMap.blue)
                            node.stroke(this.colorMap.primary)
                            node.strokeWidth(2)
                        }
                    }
                }
            })

            this.stage.batchDraw()
        },
        redrawSectors() {
            let noSectorSelected = this.noSectorSelected()
            this.sectors.forEach((sector) => {
                let node = this.sectorNodes.find((node) => node.getId() == sector.id)
                if (node) {
                    if (node.anchors && node.anchors.length) {
                        node.anchors.forEach((anchor) => {
                            anchor.destroy()
                        })
                    }
                    if (this.anchorSelectedSector && !noSectorSelected && this.isSectorSelected(sector)) {
                        node.anchors = []
                        sector.coords.forEach((coord) => {
                            let anchor = this.anchorOrigin.clone({
                                id: 'anchor',
                                x: this.imageConfig.position.x + coord.x * this.proportionX,
                                y: this.imageConfig.position.y + coord.y * this.proportionY,
                            })

                            node.anchors.push(anchor)
                            this.mainLayer.add(anchor)
                        })
                    }

                    node.points(
                        sector.coords.reduce((arr, coord) => {
                            arr.push(coord.x * this.proportionX, coord.y * this.proportionY)
                            return arr
                        }, [])
                    )
                    if (noSectorSelected || this.isSectorSelected(sector)) {
                        node.fill(this.colorMap.blue)
                    } else {
                        node.fill(this.colorMap.gray)
                    }
                }
            })

            this.stage.batchDraw()
        },
        removeSector() {
            ;[].forEach.call(arguments, (id) => {
                let node = this.sectorNodes.find((node) => node.getId() == id)
                this.sectorNodes.splice(this.sectorNodes.indexOf(node), 1)
                node.destroy()
            })
        },
        configViewpointSize() {
            // let floorplanArea = this.imageConfig.width * this.imageConfig.height * Math.pow(this.imageConfig.scale.x, 2)
            // this.dotSize = Math.sqrt((floorplanArea * 0.005) / Math.PI) / 2

            this.dotSize = 16
        },
        // deprecated
        setupViewpointOrigin() {
            this.viewpointOrigin = new Konva.Circle({
                radius: this.dotSize * 10,
                strokeWidth: (this.dotSize * 10) / 3,
                opacity: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
            })
            this.viewpointOrigin.cache()
        },
        setupViewpointOrigin2() {
            this.viewpointOrigin = new Konva.Circle({
                radius: this.dotSize * 10,
                strokeWidth: (this.dotSize * 10) / 3,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
            })

            this.innerViewpointOrigin = new Konva.Circle({
                radius: (this.dotSize - 4) * 10,
                strokeWidth: (this.dotSize * 10) / 5,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                listening: false,
            })
            this.viewpointOrigin.cache()
            this.innerViewpointOrigin.cache()
        },
        configViewpoints2() {
            if (!this.viewpointOrigin) {
                this.setupViewpointOrigin2()
            }
            this.viewpointNodes.forEach((node) => {
                node.destroy()
            })
            this.viewpointNodes = []

            let { x, y } = this.imageConfig.position
            let noViewpointSelected = this.noViewpointSelected()
            let scale = 0.1 / (4 - this.viewpointSize)

            if (this.viewpointSize == 1) {
                scale = 0.02
            }

            this.viewpoints.forEach((viewpoint, index) => {
                let isSelected = this.isViewpointSelected(viewpoint)

                let group = new Konva.Group({
                    id: viewpoint.id,
                    name: 'viewpoint',
                    x: Math.round(x + (viewpoint.x / this.image.width) * (this.imageConfig.width * this.imageConfig.scale.x)),
                    y: Math.round(y + (viewpoint.y / this.image.height) * (this.imageConfig.height * this.imageConfig.scale.x)),
                    scale: isSelected ? { x: scale * 1.25, y: scale * 1.25 } : { x: scale, y: scale },
                })

                let outerClone = null
                let innerClone = null
                let isAnIsland = this.islands ? this.islands.some((vp) => vp.id == viewpoint.id) : false
                let strokeColor = isAnIsland ? '#BC0228' : this.colorMap.primary

                if (this.grayViewpoints && this.grayViewpoints.length && this.grayViewpoints.some((id) => id == viewpoint.id)) {
                    outerClone = this.viewpointOrigin.clone({
                        id: viewpoint.id,
                        fill: this.colorMap.gray,
                        stroke: strokeColor,
                        opacity: isSelected ? 1 : 0,
                    })

                    innerClone = this.innerViewpointOrigin.clone({
                        fill: isSelected ? this.colorMap.white : this.colorMap.gray,
                        stroke: isSelected ? '' : strokeColor,
                        scale: isSelected ? { x: 0.5, y: 0.5 } : { x: 1, y: 1 },
                    })
                } else {
                    outerClone = this.viewpointOrigin.clone({
                        id: viewpoint.id,
                        fill: this.colorMap.blue,
                        stroke: strokeColor,
                        opacity: isSelected ? 1 : 0,
                    })

                    innerClone = this.innerViewpointOrigin.clone({
                        fill: isSelected ? this.colorMap.white : this.colorMap.blue,
                        stroke: isSelected ? '' : strokeColor,
                        scale: isSelected ? { x: 0.5, y: 0.5 } : { x: 1, y: 1 },
                    })
                }

                if (isAnIsland) {
                    innerClone.strokeWidth(50)
                    outerClone.strokeWidth(50)
                }

                group.add(outerClone)
                group.add(innerClone)

                this.viewpointNodes.push(group)
                this.mainLayer.add(group)
            })

            if (!noViewpointSelected) {
                this.viewpointNodes.forEach((node, index) => {
                    let viewpoint = this.viewpoints.find((vp) => vp.id == node.getId())
                    if (this.isViewpointSelected(viewpoint)) {
                        node.zIndex(this.viewpoints.length)
                    } else {
                        node.zIndex(index + 2)
                    }
                })
            }
        },

        configConnections() {
            if (this.connections && this.connections.length) {
                let connections = this.stage.find('.connection')
                connections.forEach((c) => c.destroy())

                this.connections.forEach((connection) => {
                    let nodeViewpointFrom = this.viewpointNodes.find((node) => {
                        return node.attrs.id == connection.viewpoint.id
                    })

                    if (nodeViewpointFrom) {
                        connection.connectsTo.forEach((connectTo) => {
                            let strokeColor = '#1686BE'

                            if (
                                this.grayConnections &&
                                this.grayConnections.some(
                                    (c) =>
                                        (c.from == connection.viewpoint.id && connectTo.id == c.to) ||
                                        (c.to == connection.viewpoint.id && connectTo.id == c.from)
                                )
                            ) {
                                strokeColor = '#808080'
                            }

                            if (
                                this.highlightedConnections &&
                                this.highlightedConnections.some(
                                    (c) =>
                                        (c.from == connection.viewpoint.id && connectTo.id == c.to) ||
                                        (c.to == connection.viewpoint.id && connectTo.id == c.from)
                                )
                            ) {
                                strokeColor = '#BF7E00'
                            }

                            let nodeViewpointTo = this.viewpointNodes.find((node) => node.getId() == connectTo.id)

                            if (nodeViewpointTo) {
                                let line = new Konva.Line({
                                    id: `${connection.viewpoint.id}-${connectTo.id}`,
                                    name: 'connection',
                                    x: 0,
                                    y: 0,
                                    points: [nodeViewpointFrom.x(), nodeViewpointFrom.y(), nodeViewpointTo.x(), nodeViewpointTo.y()],
                                    stroke: strokeColor,
                                    strokeWidth: this.connectionLineWidth,
                                    tension: 1,
                                })

                                this.mainLayer.add(line)
                            }
                        })
                    }
                })

                this.zIndexConfig()
            }
        },

        zIndexConfig() {
            let connections = this.stage.find('.connection')
            connections.forEach((c) => c.zIndex(1))
            this.viewpointNodes.forEach((node) => node.zIndex(connections.length + this.viewpointNodes.length))
        },

        loadTrashIconAsset() {
            return new Promise((resolve) => {
                let image = new Image()
                image.onload = () => {
                    this.trashAsset = image
                    this.trashIconNode = new Konva.Image({
                        id: 'trash-icon',
                        image: this.trashAsset,
                        scale: { x: 15 / this.trashAsset.width, y: 15 / this.trashAsset.width },
                    })
                    resolve()
                }
                image.src = trashIcon
            })
        },

        configHover() {
            this.stage.on('mouseover', (e) => {
                if (e.target.getClassName() == 'Circle' && e.target.getId() != 'anchor') {
                    let isSelected = this.selectedViewpoint == e.target.getId()

                    let outerCircle = e.target.parent.children[0]
                    let innerCircle = e.target.parent.children[1]

                    if (isSelected) {
                        var scale = 0.5
                        innerCircle.scale({ x: scale, y: scale })
                    } else {
                        var scale = 2
                        innerCircle.scale({ x: scale, y: scale })
                    }

                    if (!this.connections || (this.connections && this.connections.length == 0)) {
                        e.target.parent.zIndex(this.viewpointNodes.length)
                        outerCircle.zIndex(0)
                        innerCircle.zIndex(1)
                    }

                    e.target.draw()
                    this.stage.batchDraw()

                    this.isShowingViewpointTooltip = true

                    this.$emit('viewpointHover', { id: e.target.getId(), event: e })
                    e.target.on('mousemove', (moveEvent) => {
                        if (!this.stage.isDragging()) {
                            this.isShowingViewpointTooltip = true
                        }
                        let x = moveEvent.evt.clientX + 20
                        let y = moveEvent.evt.clientY
                        this.$refs['vp-tooltip'].style.left = x + 'px'
                        this.$refs['vp-tooltip'].style.top = y + 'px'
                    })
                } else if (e.target.getClassName() == 'Line' && e.target.getId() != 'anchor' && e.target.getName() != 'connection') {
                    let isSelected = this.selectedSector == e.target.getId()

                    e.target.zIndex(this.sectorNodes.length)
                    e.target.draw()
                    this.stage.batchDraw()

                    this.$emit('sectorHover', { id: e.target.getId(), event: e })

                    this.isShowingSectorTooltip = true
                    e.target.on('mousemove', (moveEvent) => {
                        if (!this.stage.isDragging()) {
                            this.isShowingSectorTooltip = true
                        }
                        let x = moveEvent.evt.clientX + 20
                        let y = moveEvent.evt.clientY
                        this.$refs['sector-tooltip'].style.left = x + 'px'
                        this.$refs['sector-tooltip'].style.top = y + 'px'
                    })
                }
            })

            this.stage.on('mouseout', (e) => {
                if (e.target.getClassName() == 'Circle' && e.target.getId() != 'anchor') {
                    let isSelected = this.selectedViewpoint == e.target.getId()
                    let outerCircle = e.target.parent.children[0]
                    let innerCircle = e.target.parent.children[1]

                    if (isSelected) {
                        var scale = 0.5
                        innerCircle.scale({ x: scale, y: scale })
                    } else {
                        var scale = 1
                        innerCircle.scale({ x: scale, y: scale })
                    }

                    if (!this.connections || (this.connections && this.connections.length == 0)) {
                        e.target.zIndex(this.viewpointNodes.indexOf(e.target) + 1)
                        outerCircle.zIndex(0)
                        innerCircle.zIndex(1)
                    }

                    e.target.draw()
                    this.stage.batchDraw()

                    this.$emit('viewpointHover', { id: -1, event: e })
                    this.isShowingViewpointTooltip = false
                    e.target.off('mousemove')
                } else if (e.target.getClassName() == 'Line' && e.target.getId() != 'anchor' && e.target.getName() != 'connection') {
                    e.target.zIndex(this.sectorNodes.indexOf(e.target) + 1)
                    e.target.draw()
                    this.stage.batchDraw()

                    this.$emit('sectorHover', { id: -1, event: e })

                    this.isShowingSectorTooltip = false
                    e.target.off('mousemove')
                }
            })
        },
        configClick() {
            this.stage.on('click dblclick tap dbltap mouseover', (e) => {
                let pointer = this.stage.getPointerPosition()
                let stage = this.stage.getPosition()
                let mousePos = {
                    x: Math.round((pointer.x - stage.x) / this.scale - this.imageConfig.position.x),
                    y: Math.round((pointer.y - stage.y) / this.scale - this.imageConfig.position.y),
                }

                if (e.type != 'mouseover' && this.enableConnectionsDelete) {
                    this.resetDeleteConnectionsVars()
                }

                switch (e.target.getClassName()) {
                    case 'Stage':
                        if (e.type == 'click' || e.type == 'tap') {
                            this.$emit('canvasClick', { event: e, mousePos })
                        }
                        if (e.type == 'dblclick' || e.type == 'dbltap') {
                            this.$emit('canvasDblClick', { event: e, mousePos })
                        }
                        if (e.type == 'mouseover') {
                            this.stage.container().style.cursor = 'grabbing'
                        }
                        break
                    case 'Tag':
                    case 'Text':
                        if (e.type == 'click' || e.type == 'tap') {
                            this.$emit('viewpointImgIconClick', { id: this.selectedViewpoint, event: e, mousePos })
                        }
                        if (e.type == 'dblclick' || e.type == 'dbltap') {
                            this.$emit('viewpointImgIconDblClick', { id: this.selectedViewpoint, event: e, mousePos })
                        }
                        if (e.type == 'mouseover') {
                            this.stage.container().style.cursor = 'pointer'
                        }
                        break
                    case 'Image':
                        if (e.type == 'click' || e.type == 'tap') {
                            if (this.grayscaleOnViewpointSelected) {
                                this.imageNode.opacity(this.floorplansOpacity / 100)
                                this.stage.batchDraw()
                            }
                            if (e.target.getId().includes('trash-icon')) {
                                let from = e.target.getId().split('-')[2]
                                let to = e.target.getId().split('-')[3]

                                this.$emit('removeConnection', { from, to, event: e, mousePos })
                            }

                            if (e.target.getId() == 'img-icon') {
                                this.$emit('viewpointImgIconClick', { id: this.selectedViewpoint, event: e, mousePos })
                            } else {
                                this.$emit('floorplanClick', { event: e, mousePos })
                            }
                        }
                        if (e.type == 'dblclick' || e.type == 'dbltap') {
                            if (e.target.getId() == 'img-icon') {
                                this.$emit('viewpointImgIconDblClick', { id: this.selectedViewpoint, event: e, mousePos })
                            } else {
                                this.$emit('floorplanDblClick', { event: e, mousePos })
                            }
                        }
                        if (e.type == 'mouseover') {
                            if (e.target.getId() == 'img-icon' || e.target.getId().includes('trash-icon')) {
                                this.stage.container().style.cursor = 'pointer'
                            } else {
                                this.stage.container().style.cursor = 'grab'
                            }
                        }
                        break
                    case 'Line':
                        if (e.target.getName() != 'connection') {
                            if (e.type == 'click' || e.type == 'tap') {
                                this.$emit('sectorClick', { id: e.target.getId(), event: e, mousePos })
                            }
                            if (e.type == 'dblclick' || e.type == 'dbltap') {
                                this.$emit('sectorDblClick', { id: e.target.getId(), event: e, mousePos })
                            }
                            if (e.type == 'mouseover') {
                                this.stage.container().style.cursor = 'pointer'
                            }
                        } else {
                            let from = e.target.getId().split('-')[0]
                            let to = e.target.getId().split('-')[1]

                            if (e.type == 'click' || e.type == 'tap') {
                                this.$emit('connectionClick', { from, to, event: e, mousePos })

                                if (this.enableConnectionsDelete) {
                                    // reset last nodes infos
                                    this.resetDeleteConnectionsVars()

                                    let viewpointFromId = e.target.getId().split('-')[0]
                                    let viewpointToId = e.target.getId().split('-')[1]

                                    let vpFrom = this.viewpointNodes.find((node) => node.getId() == viewpointFromId)
                                    let vpTo = this.viewpointNodes.find((node) => node.getId() == viewpointToId)

                                    let fromInnerCircle = vpFrom.children[1]
                                    let toInnerCircle = vpTo.children[1]

                                    fromInnerCircle.fill('#FFA800')
                                    fromInnerCircle.draw()

                                    toInnerCircle.fill('#FFA800')
                                    toInnerCircle.draw()

                                    e.target.stroke('#BC0228')
                                    e.target.draw()

                                    this.lastConnectionNode = e.target
                                    this.lastViewpointFromNode = vpFrom
                                    this.lastViewpointToNode = vpTo

                                    // Add trash icon
                                    let x = (vpFrom.x() + vpTo.x()) / 2 - 8
                                    let y = (vpFrom.y() + vpTo.y()) / 2 - 8
                                    this.trashIconNode.x(x)
                                    this.trashIconNode.y(y)
                                    this.trashIconNode.zIndex(400000)
                                    this.trashIconNode.id('trash-icon-' + vpFrom.getId() + '-' + vpTo.getId())

                                    this.mainLayer.add(this.trashIconNode)

                                    this.stage.batchDraw()
                                }
                            }

                            if (e.type == 'dblclick' || e.type == 'dbltap') {
                                this.$emit('connectionDblClick', { from, to, event: e, mousePos })
                            }

                            if (e.type == 'mouseover') {
                                this.stage.container().style.cursor = 'pointer'
                            }
                        }

                        break
                    case 'Circle':
                        if ((e.type == 'click' || e.type == 'tap') && e.target.getId() != 'anchor') {
                            if (this.grayscaleOnViewpointSelected) {
                                this.imageNode.opacity(this.floorplansOpacity / 100 / 2)
                                this.stage.batchDraw()
                            }
                            this.$emit('viewpointClick', { id: e.target.getId(), event: e, mousePos })
                        }
                        if ((e.type == 'dblclick' || e.type == 'dbltap') && e.target.getId() != 'anchor') {
                            this.$emit('viewpointDblClick', { id: e.target.getId(), event: e, mousePos })
                        }
                        if (e.type == 'mouseover') {
                            this.stage.container().style.cursor = 'pointer'
                        }
                        break
                }
            })
        },
        resetDeleteConnectionsVars() {
            this.trashIconNode.remove()
            if (this.lastConnectionNode) {
                this.lastConnectionNode.stroke('#1686BE')
                this.lastConnectionNode = null
            }

            if (this.lastViewpointFromNode) {
                this.lastViewpointFromNode.children[1].fill(this.colorMap.blue)
                this.lastViewpointFromNode = null
            }

            if (this.lastViewpointToNode) {
                this.lastViewpointToNode.children[1].fill(this.colorMap.blue)
                this.lastViewpointToNode = null
            }
        },
        configDrag() {
            this.stage.on('dragstart', () => {
                this.stage.container().style.cursor = 'grabbing'
                this.isShowingViewpointTooltip = false
            })

            this.stage.on('dragend', (e) => {
                if (e.target.attrs.name && e.target.attrs.name == 'viewpoint') this.$emit('viewpointPositionChanged', e.target.attrs.id)
                this.stage.container().style.cursor = 'grab'
            })
        },
        configTooltip() {
            this.tooltipNode = new Konva.Label({ cornerRadius: 4 })
        },
        showTooltip(viewpoint, viewpointNode) {
            let text = viewpoint.name
            if (text && text.length > 20) {
                text = `${text.substr(0, 17)}...`
            }

            this.imgIconNode.remove()
            this.tooltipNode.destroyChildren()

            const tagNode = new Konva.Tag({
                fill: '#151517',
                pointerDirection: 'down',
                pointerWidth: 15,
                pointerHeight: 7.5,
                lineJoin: 'round',
                cornerRadius: 4,
            })

            const textNode = new Konva.Text({
                text: `${text} |`,
                fontFamily: 'Lato',
                fontSize: 16,
                padding: 5,
                width: 500,
                fill: 'white',
            })
            const textSize = textNode.measureSize([`${text} |`])
            textNode.width(textSize.width + 30)
            this.tooltipNode.add(tagNode, textNode)

            this.imgIconNode.position({
                x: textSize.width - tagNode.attrs.width / 2 + 8,
                y: -tagNode.attrs.height / 2 - 15 + 7.5 / 4,
            })
            this.tooltipNode.add(this.imgIconNode)

            let position = viewpointNode.getPosition()
            this.tooltipNode.position({
                x: position.x,
                y: position.y - this.dotSize / (4 - this.viewpointSize) - 7.5 / 2,
            })
            this.mainLayer.add(this.tooltipNode)
        },
        noSectorSelected() {
            if (this.selectSectorByProp) {
                return !this.sectors.filter((sec) => sec.selected).length
            } else {
                return this.selectedSector == -1
            }
        },
        isSectorSelected(sector) {
            if (this.selectSectorByProp) {
                return sector.selected
            } else {
                return this.selectedSector == sector.id
            }
        },
        noViewpointSelected() {
            if (this.selectViewpointByProp) {
                return !this.viewpoints.filter((vp) => vp.selected).length
            } else {
                return !this.selectedViewpoint || this.selectedViewpoint == -1
            }
        },
        isViewpointSelected(viewpoint) {
            if (this.selectViewpointByProp) {
                return viewpoint.selected
            } else {
                return this.selectedViewpoint == viewpoint.id
            }
        },
        getViewpointProportionalPosition(id) {
            let node = this.viewpointNodes.find((node) => node.getId() == id)
            if (node) {
                let { x, y } = node.getPosition()
                return {
                    x: Math.round((x - this.imageConfig.position.x) / this.proportionX),
                    y: Math.round((y - this.imageConfig.position.y) / this.proportionY),
                }
            }
        },
        getSectorProportionalPosition(id) {
            let node = this.sectorNodes.find((node) => node.getId() == id)
            if (node) {
                let coords = []
                node.attrs.points.forEach((point, index) => {
                    // se for par, então é um x
                    if (index % 2 == 0) {
                        coords.push({
                            x: Math.round(point / this.proportionX),
                            y: Math.round(node.attrs.points[index + 1] / this.proportionY),
                        })
                    }
                })

                return coords
            }
        },
        getPinchDistance(p1, p2) {
            return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
        },
        zoomIn() {
            let pointer = { x: this.stageConfig.width / 2, y: this.stageConfig.height / 2 }
            this.scaleUp(pointer)
        },
        zoomOut() {
            let pointer = { x: this.stageConfig.width / 2, y: this.stageConfig.height / 2 }
            this.scaleDown(pointer)
        },
        resetScale() {
            this.scale = 1
            this.stage.scale({ x: this.scale, y: this.scale })
            this.stage.position({
                x: 0,
                y: 0,
            })
            this.stage.batchDraw()
        },
        scaleUp(pointer) {
            let scale = this.stage.scaleX() * this.zoomFactor
            this.scaleCanvas(scale, pointer)
        },
        scaleDown(pointer) {
            let scale = this.stage.scaleX() / this.zoomFactor
            this.scaleCanvas(scale, pointer)
        },
        scaleCanvas(scale, pointer) {
            let currentScale = this.stage.scaleX()
            let pos = { x: 0, y: 0 }

            if (scale > this.maxZoom) {
                scale = this.maxZoom
            }

            if (scale < this.minZoom) {
                scale = this.minZoom
            } else {
                pos = {
                    x: (-((pointer.x - this.stage.x()) / currentScale) + pointer.x / scale) * scale,
                    y: (-((pointer.y - this.stage.y()) / currentScale) + pointer.y / scale) * scale,
                }
            }

            this.viewpointOrigin = null
            this.configViewpoints2()
            this.applyFilters()

            this.scale = scale
            this.stage.scale({ x: scale, y: scale })
            this.stage.position(pos)
            this.stage.batchDraw()
        },
        onResize() {
            this.setupCanvasResize()
        },
    },
    async mounted() {
        Konva.showWarnings = false
        await this.setupCanvas()
        document.onresize = debounce(this.onResize, 200)
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/floorplan-canvas.scss';
</style>
